import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { invitationAPI, type InvitationValidation } from '../lib/api';
import { TokenManager } from '../lib/token-manager';
import { useBranding } from '../contexts/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Factory, Mail, Briefcase, Building, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

export default function AcceptInvitation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { branding } = useBranding();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validation, setValidation] = useState<InvitationValidation | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setError('No invitation token provided');
      setLoading(false);
    }
  }, [token]);

  const validateToken = async () => {
    try {
      setLoading(true);
      const response = await invitationAPI.validateToken(token!);
      setValidation(response.data);
    } catch (err: unknown) {
      console.error('Error validating token:', err);
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Invalid or expired invitation link');
      setValidation(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setError(null);

    // Validate passwords
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    if (password !== passwordConfirm) {
      setPasswordError('Passwords do not match');
      return;
    }

    try {
      setSubmitting(true);
      const response = await invitationAPI.acceptInvitation({
        token: token!,
        password,
        password_confirm: passwordConfirm,
      });

      // Store auth token securely
      TokenManager.setToken(response.data.token);
      TokenManager.setUser(response.data.user);

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err: unknown) {
      console.error('Error accepting invitation:', err);
      const axiosErr = err as { response?: { data?: { error?: string; password?: string[] } } };
      setError(
        axiosErr.response?.data?.error ||
        axiosErr.response?.data?.password?.[0] ||
        'Failed to create account. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-background">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (!validation?.valid || error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-red-100 dark:bg-red-900/20 rounded-full w-fit">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>{error || 'This invitation link is not valid'}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/login')} className="w-full">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const invitation = validation.invitation!;

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-background px-4 py-8">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          {branding.companyLogo ? (
            <img
              src={branding.companyLogo}
              alt={branding.companyName || invitation.company_name}
              className="mx-auto mb-4 max-h-20 max-w-[220px] object-contain"
            />
          ) : (
            <div className="mx-auto mb-4 p-3 bg-primary/10 rounded-full w-fit">
              <Factory className="h-8 w-8 text-primary" />
            </div>
          )}
          <CardTitle className="text-2xl">Welcome to {invitation.company_name}</CardTitle>
          <CardDescription>
            You've been invited to join the supplier portal
          </CardDescription>
        </CardHeader>

        <CardContent>
          {/* Invitation Details */}
          <div className="bg-gray-50 dark:bg-muted/50 rounded-lg p-6 mb-6 space-y-4">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium">Email</p>
                <p className="text-sm text-muted-foreground">{invitation.email}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Building className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium">Company</p>
                <p className="text-sm text-muted-foreground">{invitation.company_name}</p>
              </div>
            </div>

            {invitation.role_name && (
              <div className="flex items-start gap-3">
                <Briefcase className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium">User Role</p>
                  <p className="text-sm text-muted-foreground">
                    {invitation.role_name}
                    {invitation.department && ` - ${invitation.department}`}
                  </p>
                </div>
              </div>
            )}

            {invitation.message && (
              <div className="pt-3 border-t">
                <p className="text-sm font-medium mb-2">Welcome Message</p>
                <p className="text-sm text-muted-foreground italic">{invitation.message}</p>
              </div>
            )}

            <div className="pt-3 border-t">
              <p className="text-xs text-muted-foreground">
                This invitation expires on {format(new Date(invitation.expires_at), 'MMMM dd, yyyy')}
              </p>
            </div>
          </div>

          {/* Account Creation Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Create Password *</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter a secure password"
                minLength={8}
              />
              <p className="text-xs text-muted-foreground">
                Password must be at least 8 characters long
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password_confirm">Confirm Password *</Label>
              <Input
                id="password_confirm"
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
                placeholder="Re-enter your password"
                minLength={8}
              />
            </div>

            {passwordError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Accept Invitation & Create Account
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>
              By creating an account, you agree to our{' '}
              <a href="/terms" className="text-primary hover:underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

